
  import { defineComponent, reactive, ref, UnwrapRef } from 'vue'
  import { LoginService } from '@/services/LoginService'
  import { AccountService } from '@/services/AccountService'
  import { UserService } from '@/services/UserService'
  import { LoginCommandRequest } from '@/services/requests/LoginCommandRequest'
  import { CreateAccountMvoCommandRequest } from '@/services/requests/CreateAccountMvoCommandRequest'
  import { TokenService } from '@/services/TokenService'
  import { mapState, mapActions } from 'vuex'
  import {
    CheckCircleFilled
  } from '@ant-design/icons-vue'
  import { notification } from 'ant-design-vue'
  import { ValidateService } from '@/services/ValidateService'

  import ModalTerms from '../modal/modalTerms.vue'
  import ModalPolicy from '../modal/modalPolicy.vue'
  import Footer from '@/assets/components/Footer.vue'

  enum ETypeAccount {
    DEFAULT = 0,
    INDIVIDUAL = 1,
    COMPANY = 2
  }

  interface FormLogin {
    email: string | null
    password: string | null
    checked: boolean
  }

  interface FormAccount {
    name: string | null
    cpf: string | null
    email: string | null
    password: string | null
    cnpj: string | null
    nomeFantasia: string | null
  }

  export default defineComponent({
    name: 'CreateAccountMvo',
    components: {
      ModalTerms,
      ModalPolicy,
      CheckCircleFilled,
      Footer
    },
    data: () => ({
      key: '' as string | null | undefined,
      isFilledTerms: ref<boolean>(false),
      formLogin: reactive<FormLogin>({
        email: null,
        password: null,
        checked: false
      }) as UnwrapRef<FormLogin>,

      formAccount: reactive<FormAccount>({
        name: null as string | null,
        cpf: null as string | null,
        email: null as string | null,
        password: null as string | null,
        cnpj: null as string | null,
        nomeFantasia: null as string | null
      }) as UnwrapRef<FormAccount>,
      confirmEmail: null as string | null,
      rules: {
        email: [
          { required: true, message: 'Por favor insira o e-mail', trigger: 'blur' }
        ]
      },
      isMobile: ref<boolean>(false),
      isCreateAccount: ref<boolean>(false),
      isSuccessCreateAccount: ref<boolean>(false),
      isSuccessVerifyUser: ref<boolean>(false),
      isCampaignMvo: ref<boolean>(false),
      campaignMvoCode: ref(''),
      // Pessoa Jurídica habilitada por padrão
      typeAccount: ETypeAccount.COMPANY as ETypeAccount,
      typeAccountPerson: ref<boolean>(false),
      typeAccountCompany: ref<boolean>(true),
      loginService: null as LoginService | null,
      accountService: null as AccountService | null,
      userService: null as UserService | null,
      visible: ref<boolean>(false),
      textVisible: ref<number>(0),
      formRef: ref(),
      partner: '' as string | null | undefined,
      partnerHash: '' as string | null | undefined,
      clientHash: '' as string | null | undefined,
      partnerClientHash: '' as string | null | undefined,
      isPartner: ref<boolean>(false),
      version: ref<any>(process.env.VUE_APP_VERSION),
      isCustomerPartner: false,
      isFaciap: ref<boolean>(false),
      geolocation: '' as string | null,
      locationPermissionDenied: false
    }),

    async mounted () {
      if (window.innerWidth < 768) {
        this.isMobile = true
      } else {
        this.isMobile = false
      }

      this.isCreateAccount = true
      this.isSuccessCreateAccount = false
      this.isSuccessVerifyUser = false
      this.isCampaignMvo = false
      this.key = null

      const token = TokenService.getToken()
      if (token !== '') this.$router.replace('/home')

      // Validate account
      this.userService = UserService.getInstance()
      this.key = this.$route.query.key?.toString()
      if (this.key) {
        TokenService.setToken(this.key!)
        await this.verifyUser()
      }

      // Create account campaign
      this.isCampaignMvo = (this.$route.params.campaign?.toString() !== null && this.$route.params.campaign !== undefined)
      if (this.isCampaignMvo) {
        this.isCreateAccount = true
        this.campaignMvoCode = this.$route.params.campaign?.toString()
        if (this.campaignMvoCode !== 'ymclekq') this.$router.replace('/')
      }
      this.formAccount.name = this.$route.query.nome?.toString() || ''
      this.formAccount.email = this.$route.query.email?.toString() || ''

      this.partner = this.$route.query.partner?.toString()
      this.partnerHash = this.$route.query.partnerHash?.toString()
      this.partnerClientHash = this.$route.query.clientHash?.toString()

      if (this.partner && this.partnerHash) this.isCustomerPartner = true

      if (this.isCustomerPartner && this.partnerHash === 'affe9612-27b6-4c58-8923-4d8772461514') {
        this.isFaciap = true
      }

      this.accountService = AccountService.getInstance()
      this.loginService = LoginService.getInstance()
      // this.loginService.getLocation(this.setPosition)
      this.requestLocationPermission()
    },

    computed: {
      ...mapState('authenticator', ['showModalState'])
    },

    methods: {
      ...mapActions('authenticator', [
        'setEmailUsuario',
        'setAdmin',
        'setLogo',
        'setUserId',
        'setAccountId'
      ]),

      requestLocationPermission () {
        if (!navigator.geolocation) {
          this.locationPermissionDenied = true
          return
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.geolocation = `${position.coords.latitude},${position.coords.longitude}`
            this.locationPermissionDenied = false
          },
          (error) => {
            console.warn('Geolocation permission denied or error:', error)
            this.locationPermissionDenied = true
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        )
      },

      checkIsFilled () {
        let check = true
        const requiredFields: (keyof FormAccount)[] = this.typeAccountCompany
          ? ['name', 'cpf', 'email', 'password', 'cnpj', 'nomeFantasia']
          : ['name', 'cpf', 'email', 'password']

        requiredFields.forEach((field) => {
          if (!this.formAccount[field]) {
            check = false
          }
        })
        return check
      },

      checkDocumentIsValid () {
        // CPF do responsável é sempre validado, em ambos os tipos de conta
        if (!ValidateService.isValidCpf(this.formAccount.cpf || '')) {
          notification.error({
            message: 'CPF inválido',
            description: 'O CPF informado não é válido. Verifique os números digitados.'
          })
          return false
        }

        // CNPJ só é validado quando o tipo de conta é Pessoa Jurídica
        if (this.typeAccountCompany && !ValidateService.isValidCnpj(this.formAccount.cnpj || '')) {
          notification.error({
            message: 'CNPJ inválido',
            description: 'O CNPJ informado não é válido. Verifique os números digitados.'
          })
          return false
        }

        return true
      },

      AcceptTerms () {
        this.isFilledTerms = !this.isFilledTerms
      },

      setCreateAccount () {
        this.isCreateAccount = true
      },

      setLoginAccount () {
        this.isCreateAccount = false
        this.isSuccessCreateAccount = false
        this.isSuccessVerifyUser = false
        this.$router.replace('/login')
      },

      showModal (number: number) {
        this.textVisible = number
        this.visible = true
      },

      handleOk (e: MouseEvent) {
        this.visible = false
      },

      async createAccount () {
        const isFilled = this.checkIsFilled()
        if (isFilled) {
          if (!this.checkDocumentIsValid()) {
            return
          }

          if (this.isFilledTerms) {
            try {
              const request = {
                name: this.formAccount.name,
                email: this.formAccount.email?.trim().toLowerCase(),
                cpf: this.formAccount.cpf,
                password: this.formAccount.password,
                type: this.typeAccount.toString(),
                term: '',
                geolocalization: this.locationPermissionDenied ? 'NOT_AUTHORIZED' : this.geolocation || 'NOT_AVAILABLE',
                partner: null,
                partnerHash: null,
                partnerClientHash: null,
                isPartner: false
              } as CreateAccountMvoCommandRequest

              if (this.typeAccountCompany) {
                request.cnpj = this.formAccount.cnpj
                request.nomeFantasia = this.formAccount.nomeFantasia
              } else {
                request.cnpj = null
                request.nomeFantasia = null
              }

              if (this.isCustomerPartner) {
                request.partner = this.$route.query.partner?.toString() || null
                request.partnerHash = this.$route.query.partnerHash?.toString() || null
                request.partnerClientHash = this.$route.query.clientHash?.toString() || null
              }

              const res = await this.accountService?.createAccountMvo(request)

              if (res) {
                this.isSuccessCreateAccount = true
                if (this.isCustomerPartner) {
                  const dataForGatewayPlanPartner = {
                    reference: 1,
                    id: res.id,
                    name: res.name,
                    cpfCnpj: request.cpf,
                    domain: '',
                    phone: '',
                    address: '',
                    addressNumber: '',
                    zipCode: '',
                    city: '',
                    state: '',
                    district: '',
                    contact: '',
                    planId: res.planId,
                    productToken: process.env.VUE_APP_ROOT_PRODUCT_TOKEN,
                    productUrl: process.env.VUE_APP_ROOT_PRODUCT_URL,
                    isPartner: res.isPartner,
                    partnerId: res.partnerId
                  }

                  setTimeout(() => {
                    const dataForGatewayPlanPartnerEncode = btoa(JSON.stringify(dataForGatewayPlanPartner))
                    window.location.href = process.env.VUE_APP_ROOT_GATEWAY_URL + '/plans?data=' + dataForGatewayPlanPartnerEncode
                  }, 5000)
                }
              } else {
                notification.error({
                  message: 'Falha no Cadastro',
                  description: 'Não foi possivel criar sua conta!'
                })
              }
            } catch (err) {
              console.error(err)
            }
          } else {
            notification.error({
              message: 'Falha no Cadastro',
              description: 'Você precisa concordar com os Termos de Uso e Política de Privacidade!'
            })
          }
        } else {
          notification.error({
            message: 'Falha no Cadastro',
            description: 'Os Campos obrigatórios precisam ser preenchidos!'
          })
        }
      },

      async verifyUser () {
        try {
          await this.userService?.verify()
          this.isSuccessVerifyUser = true
        } catch (e) {
          this.isSuccessVerifyUser = false
        }
        TokenService.clear()
      },

      checkTypePerson (checked: boolean) {
        if (checked) {
          this.typeAccountCompany = false
          this.typeAccount = ETypeAccount.INDIVIDUAL
        } else {
          this.typeAccountPerson = false
          this.typeAccountCompany = true
          this.typeAccount = ETypeAccount.COMPANY
        }
      },

      checkTypeCompany (checked: boolean) {
        if (checked) {
          // Ativou Pessoa Jurídica -> desativa Pessoa Física
          this.typeAccountPerson = false
          this.typeAccount = ETypeAccount.COMPANY
        } else {
          // Tentou desativar Pessoa Jurídica -> ativa Pessoa Física automaticamente
          this.typeAccountCompany = false
          this.typeAccountPerson = true
          this.typeAccount = ETypeAccount.INDIVIDUAL
        }
      }
    }
  })
